/**
 * useCustomDomainBrand — client-facing hook that resolves the
 * studio's brand overrides when the visitor came in through a
 * verified custom hostname.
 *
 * Wraps the pre-existing `useCurrentCustomDomain` service hook so
 * every public route (`/delivery/:slug`, `/portal/:token`, `/video/
 * :token`, `/sign/:token`, `/book/:slug`) can drop into the same
 * `CustomDomainProvider` and get:
 *
 *   • `isCustomDomain: boolean` — quick "am I on a studio host?"
 *     check for conditional rendering.
 *   • `brand` — narrowed slice of the settings row (logo, primary,
 *     accent, font, hide-footer, org_id) with sensible nulls.
 *
 * Also exports `useCustomDomainCrossOrgGuard` — the redirect
 * enforcer for pages that carry a slug / token belonging to a
 * specific org. If a studio's verified hostname is used to serve
 * ANOTHER studio's content, the guard redirects to the canonical
 * `orbit.app/...` URL. Prevents brand laundering (studio X points
 * DNS at us then links to studio Y's private delivery to make it
 * look like theirs).
 *
 * Kept as a hook — router-level middleware isn't a thing in Orbit's
 * Vite/SPA setup, and the guard needs access to the slug from
 * `useParams` which only lives inside a Route.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentCustomDomain, type PublicCustomDomain } from '@/services/customDomain';
import { supabase } from '@/integrations/supabase/client';
import { isOrbitOwnedHost, APP_ORIGIN } from '@/lib/hostRouting';

export interface CustomDomainBrand {
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  // Wave 6.1 — bg + fg now flow through to the provider so the
  // studio's chosen background / foreground actually paint on
  // portal pages instead of defaulting to Orbit's palette.
  bgColor: string | null;
  fgColor: string | null;
  fontFamily: string | null;
  hideOrbitFooter: boolean;
  orgId: string | null;
}

export interface UseCustomDomainBrandResult {
  isCustomDomain: boolean;
  brand: CustomDomainBrand | null;
  isLoading: boolean;
}

export function useCustomDomainBrand(): UseCustomDomainBrandResult {
  const { data, isLoading } = useCurrentCustomDomain();

  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : null;
  const isCustomDomain =
    !!hostname && !isOrbitOwnedHost(hostname) && !!data;

  const brand: CustomDomainBrand | null = data
    ? mapDomainToBrand(data)
    : null;

  return { isCustomDomain, brand, isLoading };
}

function mapDomainToBrand(d: PublicCustomDomain): CustomDomainBrand {
  return {
    logoUrl: d.brand_logo_url,
    primaryColor: d.brand_primary_color,
    accentColor: d.brand_accent_color,
    bgColor: d.brand_bg_color,
    fgColor: d.brand_fg_color,
    fontFamily: d.brand_font_family,
    hideOrbitFooter: d.hide_orbit_footer,
    orgId: d.org_id,
  };
}

// ─── Cross-org guard ─────────────────────────────────────────────

interface GuardArgs {
  /** Which surface — determines which RPC we call to resolve the
   *  content's owning org. `gallery` was added in GAL-2 for the
   *  standalone `/gallery/:galleryId` route; it resolves the org
   *  via the gallery's project. */
  kind: 'delivery' | 'portal' | 'gallery';
  /** The slug (deliveries), token (portal), or gallery id
   *  (gallery). Missing → guard is a no-op (page still loading
   *  route params). */
  slug: string | undefined;
  /** Toggle the guard off entirely (e.g. while an auth check
   *  hasn't fired yet). */
  enabled?: boolean;
}

/**
 * If the visitor is on a verified custom hostname AND the surface
 * they're viewing belongs to a different org, redirect to the
 * canonical Orbit URL.
 *
 * The guard is intentionally noisy in the console — the caller
 * shouldn't need to observe it explicitly, but a studio admin
 * inspecting a "wait, why did we redirect?" bug report should
 * find a trail.
 */
export function useCustomDomainCrossOrgGuard({
  kind,
  slug,
  enabled = true,
}: GuardArgs): void {
  const { brand, isCustomDomain, isLoading } = useCustomDomainBrand();

  // Fetch the surface's owning org via the new Wave 6 RPCs. Only
  // runs when we're actually on a custom hostname (Orbit-owned
  // hosts skip the query entirely).
  const { data: surfaceOrgId } = useQuery({
    queryKey: ['surface-org-id', kind, slug],
    queryFn: async (): Promise<string | null> => {
      if (!slug) return null;
      // GAL-2 — gallery kind resolves via `get_gallery_org_id`. The
      // RPC ships in the same migration as `get_public_gallery_by_id`
      // so the guard can't fire before its backing lookup exists.
      const fn =
        kind === 'delivery'
          ? 'get_delivery_org_id'
          : kind === 'gallery'
          ? 'get_gallery_org_id'
          : 'get_project_org_id_by_token';
      const arg =
        kind === 'delivery'
          ? { p_slug: slug }
          : kind === 'gallery'
          ? { p_gallery_id: slug }
          : { p_token: slug };
      const { data, error } = await (supabase as any).rpc(fn, arg);
      if (error) throw error;
      return (data ?? null) as string | null;
    },
    enabled: !!slug && enabled && isCustomDomain && !isLoading,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!enabled) return;
    if (isLoading) return;
    if (!isCustomDomain) return;
    if (!brand?.orgId) return;
    if (!surfaceOrgId) return;
    if (surfaceOrgId === brand.orgId) return;

    // GAL-DOMAIN-1 — this fallback was `https://orbit.app`, a
    // placeholder domain from the original Wave OG-1 assumptions
    // (see docs/apple-app-site-association.md) that Orbit never
    // actually owns — `orbit.app` isn't in ORBIT_EXACT_HOSTS, and
    // VITE_CANONICAL_ORIGIN is referenced nowhere else in the repo,
    // so it was NEVER actually configured anywhere. Every cross-org
    // redirect (real for any studio on a verified custom domain)
    // was silently sending the visitor to a domain Orbit doesn't
    // run, reading as "the link doesn't load." APP_ORIGIN
    // (orbitstudiocrm.com) is the real canonical app host.
    const canonicalOrigin =
      (import.meta as any).env?.VITE_CANONICAL_ORIGIN ?? APP_ORIGIN;
    const path = window.location.pathname + window.location.search;
    console.warn(
      '[custom-domain] cross-org access blocked — redirecting to canonical',
      { hostOrg: brand.orgId, surfaceOrg: surfaceOrgId, path },
    );
    window.location.replace(`${canonicalOrigin}${path}`);
  }, [enabled, isLoading, isCustomDomain, brand?.orgId, surfaceOrgId]);
}
